<template>
  <div class="repair-stats-page">
    <el-card class="box-card" shadow="never">
      <div slot="header" class="clearfix header-bar">
        <span class="page-title">接单数据统计</span>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-refresh-right" :loading="loading" @click="fetchAll">刷新</el-button>
        </div>
      </div>

      <!-- 查询区：label 13px、控件 size=small（与报修列表页 RepairList.vue 保持一致） -->
      <el-form :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
        <el-form-item label="接单人">
          <el-select v-model="queryForm.accept_by" placeholder="全部" clearable filterable style="width: 140px">
            <el-option v-for="name in acceptorOptions" :key="name" :label="name" :value="name" />
          </el-select>
        </el-form-item>
        <el-form-item label="报修日期">
          <el-date-picker
            v-model="queryForm.dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item label="工单状态">
          <el-select v-model="queryForm.status" placeholder="全部" clearable style="width: 130px">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item class="search-buttons">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 说明条：点卡片只改明细表的过滤范围，统计卡之间仍可横向对比 -->
      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="legend-alert"
        title="统计卡按接单人聚合：显示报修日期/工单状态筛选后的数据（不筛选即全量），待接单工单不计入任何人。点击卡片＝明细表按该接单人过滤，再点一次取消。"
      />

      <!-- 接单人统计卡：一行横排，按 accept_by_name 分组聚合 -->
      <div v-loading="loading" class="stats-row">
        <div
          v-for="card in statCards"
          :key="card.name"
          :class="['stat-card', { 'stat-card-active': selectedAcceptor === card.name }]"
          @click="toggleAcceptor(card.name)"
        >
          <div class="stat-name">{{ card.name }}</div>
          <div class="stat-grid">
            <div class="stat-item">
              <div class="stat-num">{{ card.total }}</div>
              <div class="stat-label">接单总数</div>
            </div>
            <div class="stat-item">
              <div class="stat-num stat-num-success">{{ card.done }}</div>
              <div class="stat-label">已完成</div>
            </div>
            <div class="stat-item">
              <div class="stat-num stat-num-primary">{{ card.doing }}</div>
              <div class="stat-label">进行中</div>
            </div>
            <div class="stat-item">
              <div class="stat-num stat-num-warning">{{ card.returnCount }}</div>
              <div class="stat-label">回退次数</div>
            </div>
          </div>
          <div class="stat-foot">配件金额合计 <span class="stat-money">¥{{ money(card.partsAmount) }}</span></div>
        </div>
        <div v-if="!loading && !statCards.length" class="stat-empty">当前筛选条件下暂无接单记录</div>
      </div>

      <!-- 工单明细：受「查询区条件 + 选中接单人」双重过滤，前端分页（数据已全量在内存） -->
      <div class="detail-title">
        工单明细
        <span class="detail-tip">
          共 {{ filteredRows.length }} 条<template v-if="selectedAcceptor">（已按接单人「{{ selectedAcceptor }}」过滤）</template>
        </span>
      </div>
      <el-table :data="pagedRows" border size="small" class="stats-table">
        <el-table-column type="index" :index="rowIndex" label="序号" width="55" align="center" />
        <el-table-column prop="order_no" label="工单号" width="150" show-overflow-tooltip />
        <el-table-column prop="equipment_name" label="设备名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.equipment_name || '-' }}</span>
            <el-tag v-if="scope.row.is_emergency" type="danger" size="mini" class="mark-tag">急救</el-tag>
            <el-tag v-else-if="scope.row.is_urgent" type="warning" size="mini" class="mark-tag">加急</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="department" label="科室" width="120" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.department || '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTagType(scope.row.status)" size="small">{{ statusText(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="accept_by_name" label="接单人" width="110" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.accept_by_name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="accepted_at" label="接单时间" width="150" align="center">
          <template slot-scope="scope">{{ scope.row.accepted_at || '-' }}</template>
        </el-table-column>
        <el-table-column prop="finished_at" label="完毕日期" width="150" align="center">
          <template slot-scope="scope">{{ scope.row.finished_at || '-' }}</template>
        </el-table-column>
        <el-table-column label="回退次数" width="80" align="center">
          <template slot-scope="scope">
            <span :class="{ 'return-count': scope.row.return_count > 0 }">{{ scope.row.return_count || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column label="配件金额" width="110" align="right">
          <template slot-scope="scope">¥{{ money(partsAmount(scope.row)) }}</template>
        </el-table-column>
        <template slot="empty">
          <span v-if="backendError">后端未启动或接口异常，错误信息已弹窗提示</span>
          <span v-else>暂无匹配的工单</span>
        </template>
      </el-table>

      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next"
        :total="filteredRows.length"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="currentPage"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </el-card>
  </div>
</template>

<script>
import repairApi from '../api/repair'

// 工单状态枚举（与后端/报修列表页保持一致）
const STATUS_OPTIONS = [
  { value: 'PENDING', label: '待接单' },
  { value: 'PROCESSING', label: '维修中' },
  { value: 'SUSPENDED', label: '挂起' },
  { value: 'WAIT_SIGN', label: '待签字' },
  { value: 'SIGNED', label: '已关闭' },
  { value: 'CANCELLED', label: '已撤销' },
]
const STATUS_TEXT = {
  PENDING: '待接单',
  PROCESSING: '维修中',
  SUSPENDED: '挂起',
  WAIT_SIGN: '待签字',
  SIGNED: '已关闭',
  CANCELLED: '已撤销',
}
// 「进行中」口径：维修中 / 挂起 / 待签字
const DOING_STATUS = ['PROCESSING', 'SUSPENDED', 'WAIT_SIGN']

export default {
  name: 'RepairStats',
  data() {
    return {
      allRows: [], // 全量工单（循环翻页一次性拉齐，之后所有统计/分页都在前端做）
      loading: false,
      backendError: false,
      statusOptions: STATUS_OPTIONS,
      queryForm: {
        accept_by: '',
        dateRange: ['', ''],
        status: '',
      },
      // 点统计卡选中的接单人（空串＝不过滤）
      selectedAcceptor: '',
      currentPage: 1,
      pageSize: 10,
    }
  },
  computed: {
    // 接单人下拉选项：从全量数据聚合去重（全量＝不受任何查询条件影响，保证选项稳定）
    acceptorOptions() {
      const set = {}
      this.allRows.forEach(r => {
        if (r.accept_by_name) set[r.accept_by_name] = true
      })
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'))
    },
    // 统计卡数据源：只应用「报修日期 + 工单状态」，不应用「接单人」下拉与选中的卡片，
    // 这样注释所述：选人时各卡之间仍能横向对比。不筛选时即为全量数据。
    cardRows() {
      return this.allRows.filter(r => this.matchDate(r) && this.matchStatus(r))
    },
    // 统计卡：按 accept_by_name 分组聚合；待接单（无人接单）不计入任何人
    statCards() {
      const map = {}
      this.cardRows.forEach(r => {
        const name = r.accept_by_name
        if (!name) return
        if (!map[name]) {
          map[name] = { name, total: 0, done: 0, doing: 0, returnCount: 0, partsAmount: 0 }
        }
        const item = map[name]
        item.total += 1
        if (r.status === 'SIGNED') item.done += 1
        if (DOING_STATUS.indexOf(r.status) !== -1) item.doing += 1
        item.returnCount += Number(r.return_count) || 0
        item.partsAmount += this.partsAmount(r)
      })
      // 按接单总数从多到少排，多的排前面
      return Object.keys(map)
        .map(k => map[k])
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'zh-Hans-CN'))
    },
    // 明细表数据：查询区三个条件 + 选中接单人，双重过滤
    filteredRows() {
      const acceptor = this.selectedAcceptor || this.queryForm.accept_by
      return this.allRows.filter(
        r => this.matchDate(r) && this.matchStatus(r) && (!acceptor || r.accept_by_name === acceptor)
      )
    },
    // 前端分页切片
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredRows.slice(start, start + this.pageSize)
    },
  },
  created() {
    this.fetchAll()
  },
  methods: {
    // 全量拉取：page_size=100 循环翻页直到拉满 count（思路同 RepairList.vue 的 fetchAllRows）
    async fetchAll() {
      this.loading = true
      this.backendError = false
      const rows = []
      const pageSize = 100
      let page = 1
      let total = Infinity
      try {
        while ((page - 1) * pageSize < total) {
          const res = await repairApi.listOrders({ page, page_size: pageSize })
          const data = res.data
          const part = data.results || data
          rows.push(...part)
          total = data.count !== undefined ? data.count : part.length
          if (!part.length) break
          page += 1
        }
        this.allRows = rows
      } catch (e) {
        // 失败不静默：把后端返回的 detail 直接透出（详情页字段名/校验错误通常藏在这里）
        this.backendError = true
        this.allRows = []
        this.$message.error(this.errorText(e))
      } finally {
        this.loading = false
      }
    },
    // 从异常里提取可读信息：优先后端 detail / 字段错误，其次 HTTP 状态行，最后 error.message
    errorText(e) {
      const resp = e && e.response && e.response.data
      if (resp) {
        if (typeof resp === 'string') return resp
        if (resp.detail) return String(resp.detail)
        const firstKey = Object.keys(resp)[0]
        if (firstKey) {
          const v = resp[firstKey]
          return `${firstKey}：${Array.isArray(v) ? v.join('；') : String(v)}`
        }
      }
      if (e && e.response && e.response.status) return `请求失败（HTTP ${e.response.status}）`
      return (e && e.message) || '未知错误'
    },
    handleSearch() {
      this.currentPage = 1 // 查询必须把页码重置到第一页
      this.selectedAcceptor = ''
    },
    handleReset() {
      this.queryForm = { accept_by: '', dateRange: ['', ''], status: '' }
      this.selectedAcceptor = ''
      this.currentPage = 1
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
    },
    handlePageChange() {
      // 前端分页：数据全在内存，翻页不需要重新请求
    },
    // 点统计卡：选中→明细表按该接单人过滤；再点一次取消
    toggleAcceptor(name) {
      this.selectedAcceptor = this.selectedAcceptor === name ? '' : name
      this.currentPage = 1
    },
    // 报修日期过滤：按 created_at 的日期部分比较（created_at 形如 2026-01-02 15:04:05）
    matchDate(row) {
      const [start, end] = this.queryForm.dateRange || []
      if (!start && !end) return true
      const day = (row.created_at || '').slice(0, 10)
      if (!day) return false
      if (start && day < start) return false
      if (end && day > end) return false
      return true
    },
    matchStatus(row) {
      return !this.queryForm.status || row.status === this.queryForm.status
    },
    // 单行配件金额：parts[].amount 求和，无配件为 0
    partsAmount(row) {
      const parts = row.parts || []
      return parts.reduce((sum, p) => sum + (Number(p && p.amount) || 0), 0)
    },
    money(v) {
      return (Number(v) || 0).toFixed(2)
    },
    rowIndex(idx) {
      return (this.currentPage - 1) * this.pageSize + idx + 1
    },
    statusText(row) {
      return (row && row.status_display) || STATUS_TEXT[row.status] || '-'
    },
    statusTagType(status) {
      const map = {
        PENDING: 'warning',
        PROCESSING: 'primary',
        SUSPENDED: 'info',
        WAIT_SIGN: 'warning',
        SIGNED: 'success',
        CANCELLED: 'info',
      }
      return map[status] || 'info'
    },
  },
}
</script>

<style scoped>
.repair-stats-page {
  padding: 16px;
}
.box-card {
  width: 100%;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
.header-actions {
  display: flex;
  gap: 8px;
}
/* 查询区：label 13px、控件 13px/高 32px，与报修列表页统一 */
.search-form /deep/ .el-form-item__label {
  font-weight: bold;
  font-size: 13px;
}
.search-form /deep/ .el-input__inner {
  font-size: 13px;
  height: 32px;
  line-height: 32px;
  color: #303133;
}
.search-form /deep/ .el-input__inner::placeholder {
  font-size: 13px;
}
.search-form /deep/ .el-input__icon {
  line-height: 32px;
}
.search-form /deep/ .el-range-editor.el-input__inner {
  padding: 0 8px;
}
.search-form /deep/ .el-date-editor .el-range-separator {
  line-height: 26px;
}
.search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px 0 12px;
}
.search-form .el-form-item {
  margin-bottom: 10px;
}
.search-buttons {
  margin-left: auto;
}
.legend-alert {
  margin-bottom: 12px;
}
/* 统计卡：一行横排，放不下自动换行 */
.stats-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  min-height: 60px;
}
.stat-card {
  width: 200px;
  padding: 12px 14px;
  border: 1px solid #e0d9c8;
  border-radius: 6px;
  background-color: #fffdf8;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.stat-card:hover {
  border-color: #d8a657;
}
/* 选中的卡片高亮边框 */
.stat-card-active {
  border-color: #e6a23c;
  box-shadow: 0 0 0 2px rgba(230, 162, 60, 0.25);
}
.stat-name {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}
.stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 8px;
}
.stat-item {
  display: flex;
  flex-direction: column;
}
.stat-num {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  line-height: 1.2;
}
.stat-num-success {
  color: #67c23a;
}
.stat-num-primary {
  color: #409eff;
}
.stat-num-warning {
  color: #e6a23c;
}
.stat-label {
  font-size: 12px;
  color: #909399;
}
.stat-foot {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e6e0d2;
  font-size: 12px;
  color: #606266;
}
.stat-money {
  font-weight: bold;
  color: #e6a23c;
}
.stat-empty {
  color: #909399;
  font-size: 13px;
  line-height: 60px;
}
.detail-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}
.detail-tip {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 8px;
}
.mark-tag {
  margin-left: 4px;
}
.return-count {
  color: #e6a23c;
  font-weight: bold;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
</style>
